import { useEffect, useRef, useState } from "react";
import styles from "./NoteDialog.module.css";

interface Props {
  open: boolean;
  isSubmitting: boolean;
  heading: string;
  itemName: string;
  helpText: string;
  placeholder: string;
  submitLabel: string;
  onCancel: () => void;
  onSubmit: (note: string) => void;
  // When true the note may be left blank (e.g. an optional "mark done" comment).
  optional?: boolean;
  // The field's label, and what a blank required note is told.
  fieldLabel?: string;
  requiredMessage?: string;
  // False for a plain confirmation with no note at all.
  withNote?: boolean;
  // Why the last submit failed, shown under the field.
  submitError?: string | null;
  // The submit button's look: "danger" for a removal or a clear.
  tone?: "primary" | "danger";
  initialNote?: string;
}

// Modal note prompt for the Amazon overview's item actions (owners, answers,
// bulk changes). A note can run several lines, so it gets a textarea.
export const NoteDialog = ({
  open,
  isSubmitting,
  heading,
  itemName,
  helpText,
  placeholder,
  submitLabel,
  onCancel,
  onSubmit,
  optional = false,
  fieldLabel = "Note",
  requiredMessage = "A short note is required - it records why this was closed.",
  withNote = true,
  submitError = null,
  tone = "primary",
  initialNote = "",
}: Props) => {
  const ref = useRef<HTMLDialogElement | null>(null);
  const [note, setNote] = useState<string>("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) {
      setNote(initialNote);
      setError(null);
      dialog.showModal();
    } else if (!open && dialog.open) {
      dialog.close();
    }
  }, [open, initialNote]);

  const handleSubmit = () => {
    if (withNote && !optional && !note.trim()) {
      setError(requiredMessage);
      return;
    }
    onSubmit(note.trim());
  };

  return (
    <dialog
      ref={ref}
      className={styles.dialog}
      onCancel={(e) => {
        e.preventDefault();
        onCancel();
      }}
    >
      <form
        method="dialog"
        className={styles.form}
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit();
        }}
      >
        <h2 className={styles.heading}>{heading}</h2>
        <p className={styles.itemRef}>
          <strong>{itemName}</strong>
        </p>
        <p className={styles.help}>{helpText}</p>

        {withNote && (
        <label className={styles.field}>
          <span className={styles.label}>
            {fieldLabel}{" "}
            <span className={styles.required}>
              ({optional ? "optional" : "required"})
            </span>
          </span>
          <textarea
            className={styles.textarea}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={placeholder}
            rows={5}
            disabled={isSubmitting}
            autoFocus
          />
        </label>
        )}

        {(error ?? submitError) && <div className={styles.error}>{error ?? submitError}</div>}

        <div className={styles.actions}>
          <button
            type="button"
            className={styles.btnCancel}
            onClick={onCancel}
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            className={tone === "danger" ? styles.btnDanger : styles.btnSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? "Saving..." : submitLabel}
          </button>
        </div>
      </form>
    </dialog>
  );
};
